'use client';
import { CaptchaRequestMessage } from '@eliadmin/core/src/components/providers/captcha-form-message';

import { useId } from 'react';

import Link from '@eliadmin/core/src/components/link';
import { REGISTER_COPY } from '@eliadmin/core/src/components/dlz/auth/register/register-copy';
import {
  formatCountdown,
  useResendVerification,
} from '@eliadmin/core/src/components/dlz/auth/register/use-resend-verification';
import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Container } from '@eliadmin/core/src/components/ui/container';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';

/**
 * Figma „Učet vytvorený“ (60260:30410): confirmation after the registration. The customer id for
 * „Odoslať aktivačný link znovu“ comes from the customer store (set by the registration, restored
 * from the auth cookie after a reload); without a signed-in customer the resend is not offered.
 */
export function RegisterSuccess() {
  const { t } = useTranslations();
  const titleId = useId();
  const customerId = useCustomerStore(state => state.me?.customer.id);
  const isInitialized = useCustomerStore(state => state.isInitialized);
  const { status, remaining, send, error } = useResendVerification(customerId);
  const copy = REGISTER_COPY.success;
  const cooling = remaining > 0;

  return (
    <section
      data-section="register-success"
      aria-labelledby={titleId}
      className="pb-6 pt-12 md:pb-20 md:pt-20"
    >
      <Container>
        <div className="mx-auto flex max-w-[878px] flex-col items-center gap-6">
          <Icon name="check-circle" size={52} className="text-[#22A04B]" />
          <div className="flex flex-col items-center gap-4 text-center">
            <h1 id={titleId} className="dlz-h2 text-[#0F1012]">
              {copy.title}
            </h1>
            <p className="dlz-body-m max-w-[426px] text-[#4F545C]">{copy.text}</p>
          </div>
          <div className="relative flex w-full flex-col items-center gap-3 md:w-auto md:flex-row">
            <Button asChild className="h-12 w-full md:h-[52px] md:w-auto">
              <Link href={ROUTES.SIGN_IN()} prefetch={false}>
                {t('text189')}
              </Link>
            </Button>
            {customerId !== undefined || !isInitialized ? (
              <Button
                type="button"
                variant="secondary"
                onClick={send}
                isLoading={status === 'sending'}
                disabled={customerId === undefined || cooling}
                className="h-12 w-full md:h-[52px] md:w-auto"
              >
                {copy.resend}
              </Button>
            ) : null}
            <div className="absolute inset-x-0 top-full mt-3 text-center md:mt-4">
              <p
                role={status === 'failed' ? 'alert' : 'status'}
                className={
                  status === 'failed' ? 'dlz-body-s text-[#D7191F]' : 'dlz-body-s text-[#16753A]'
                }
              >
                {status === 'sent' ? copy.sent : status === 'failed' ? copy.failed : null}
              </p>
              <CaptchaRequestMessage error={error} />
              {cooling ? (
                <p aria-hidden="true" className="dlz-body-s text-[#6B7079]">
                  {copy.cooldown} {formatCountdown(remaining)}
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}
